import { useEffect, useState, useCallback } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { kes } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Landmark, TriangleAlert, Loader2, FileText, Upload } from "lucide-react";

export default function Banking() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sel, setSel] = useState(() => new Set());

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([api.get("/banking/status"), api.get("/banking")]);
      setStatus(s.data);
      setHistory(h.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !status) {
    return <div className="flex py-20 justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  // a shift picked earlier may have been banked since: keep only the ones still listed
  const picked = status.shifts.filter((s) => sel.has(s.shift_id));
  const pickedCash = picked.reduce((a, s) => a + s.actual_cash, 0);
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = status.shifts.length > 0 && picked.length === status.shifts.length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Cash Banking</h1>
        <p className="text-sm text-muted-foreground">Reconcile accumulated shift cash against what's actually deposited at the bank.</p>
      </div>

      {status.alert && (
        <Card className="flex items-center gap-3 border-rose-300 bg-rose-50 p-4">
          <TriangleAlert className="h-6 w-6 flex-shrink-0 text-rose-600" />
          <div>
            <div className="font-semibold text-rose-800">Cash on hand has reached the alert threshold</div>
            <div className="text-sm text-rose-700">KSh {kes(status.cash_on_hand)} is currently un-banked (threshold: KSh {kes(status.threshold)}). Consider banking soon.</div>
          </div>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Cash On Hand</div>
          <div className={`mt-1 font-mono-fin text-2xl font-bold ${status.alert ? "text-rose-600" : "text-slate-900"}`}>KSh {kes(status.cash_on_hand)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Alert Threshold</div>
          <div className="mt-1 font-mono-fin text-2xl font-bold text-slate-900">KSh {kes(status.threshold)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Un-banked Shifts</div>
          <div className="mt-1 font-mono-fin text-2xl font-bold text-slate-900">{status.shifts.length}</div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b bg-slate-50 px-4 py-3">
          <h3 className="font-display font-semibold text-slate-800">Shifts Awaiting Banking</h3>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-brand hover:bg-brand/90" disabled={picked.length === 0} data-testid="record-banking-button">
                <Landmark className="mr-1.5 h-4 w-4" />{picked.length === 0 ? "Select shifts to bank" : `Record Banking · ${picked.length} shift${picked.length === 1 ? "" : "s"}`}
              </Button>
            </DialogTrigger>
            <RecordBankingDialog expected={pickedCash} count={picked.length} shiftIds={picked.map((s) => s.shift_id)}
              onOpenChange={setDialogOpen} onDone={() => setSel(new Set())} reload={load} />
          </Dialog>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(status.shifts.map((s) => s.shift_id)))} aria-label="Select all shifts" /></TableHead>
              <TableHead>Date</TableHead><TableHead>Period</TableHead><TableHead>Supervisor</TableHead>
              <TableHead className="text-right">Already banked</TableHead>
              <TableHead className="text-right">Cash left to bank</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {status.shifts.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">All caught up — nothing awaiting banking.</TableCell></TableRow>
            )}
            {status.shifts.map((s) => (
              <TableRow key={s.shift_id} data-testid={`unbanked-shift-${s.shift_id}`} className={sel.has(s.shift_id) ? "bg-indigo-50" : ""}>
                <TableCell><input type="checkbox" checked={sel.has(s.shift_id)} onChange={() => toggle(s.shift_id)} aria-label={`Select ${s.date} ${s.shift_type}`} /></TableCell>
                <TableCell>{s.date}</TableCell>
                <TableCell>{s.shift_type}</TableCell>
                <TableCell>{s.supervisor_name || "—"}</TableCell>
                <TableCell className="text-right font-mono-fin text-muted-foreground">{s.already_banked ? kes(s.already_banked) : "—"}</TableCell>
                <TableCell className="text-right font-mono-fin">{kes(s.actual_cash)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-4 py-3">
          <h3 className="font-display font-semibold text-slate-800">Banking History</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Shifts</TableHead>
              <TableHead className="text-right">Expected</TableHead><TableHead className="text-right">Deposited</TableHead>
              <TableHead className="text-right">Variance</TableHead><TableHead>Status</TableHead>
              <TableHead>By</TableHead><TableHead>Slip</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">No banking records yet.</TableCell></TableRow>
            )}
            {history.map((b) => (
              <TableRow key={b._id} data-testid={`banking-row-${b._id}`}>
                <TableCell className="text-xs">{(b.banked_at || "").slice(0, 10)}</TableCell>
                <TableCell>{b.shift_lines?.length ?? b.shift_ids?.length ?? 0}</TableCell>
                <TableCell className="text-right font-mono-fin">{kes(b.expected_amount)}</TableCell>
                <TableCell className="text-right font-mono-fin">{kes(b.deposited_amount)}</TableCell>
                <TableCell className={`text-right font-mono-fin ${Math.abs(b.variance) < 0.01 ? "text-emerald-600" : "text-rose-600"}`}>
                  {b.variance >= 0 ? "+" : ""}{kes(b.variance)}
                </TableCell>
                <TableCell>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${b.verified ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {b.partial ? "Part banking" : b.verified ? "Verified" : "Mismatch"}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{b.banked_by}</TableCell>
                <TableCell>
                  {b.slip_filename ? (
                    <a href={`${API}/banking/${b._id}/slip`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
                      <FileText className="h-3.5 w-3.5" />View
                    </a>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

const PAYOUT_CATEGORIES = ["Staff advance", "Salaries & wages", "Rent", "Utilities", "Bank charges", "Generator fuel", "Station expenses"];

function RecordBankingDialog({ expected, count, shiftIds, onOpenChange, onDone, reload }) {
  const [payouts, setPayouts] = useState([]);
  const setPayout = (i, patch) => setPayouts((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const paidOut = payouts.reduce((t, p) => t + (parseFloat(p.amount) || 0), 0);
  const [part, setPart] = useState(false);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [bank, setBank] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!amount) return toast.error("Enter the deposited amount from the bank slip");
    if (!bank) return toast.error("Choose the bank the cash went into");
    if (payouts.some((p) => parseFloat(p.amount) && !p.category)) return toast.error("Choose a category for each cash payout");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("deposited_amount", amount);
      form.append("payouts", JSON.stringify(payouts));
      form.append("notes", notes);
      form.append("bank", bank);
      form.append("shift_ids", shiftIds.join(","));
      form.append("partial", part ? "true" : "false");
      if (file) form.append("slip", file);
      const { data } = await api.post("/banking", form);
      const variance = data.variance;
      if (data.partial) {
        toast.success(`Part banking recorded — KSh ${kes(Math.max(0, expected - data.deposited_amount - (data.payout_total || 0)))} of these shifts' cash is still to bank.`);
      } else if (Math.abs(variance) < 0.01) {
        toast.success(`Banking recorded — verified, matches expected exactly.`);
      } else {
        toast.warning(`Banking recorded with a variance of KSh ${kes(variance)} vs expected.`);
      }
      setAmount(""); setNotes(""); setBank(""); setFile(null); setPart(false); setPayouts([]);
      onOpenChange(false);
      onDone && onDone();
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent data-testid="banking-dialog">
      <DialogHeader>
        <DialogTitle className="font-display">Record Banking</DialogTitle>
        <DialogDescription>Covers the {count} shift{count === 1 ? "" : "s"} you selected. Enter what the bank slip shows — the system checks it against what was expected.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div className="rounded-lg bg-slate-50 p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Expected (from shifts)</span>
            <span className="font-mono-fin font-semibold">KSh {kes(expected)}</span>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Bank *</Label>
          <select className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={bank} onChange={(e) => setBank(e.target.value)} data-testid="banking-bank-select">
            <option value="">— choose the bank —</option>
            <option value="ABSA">ABSA</option>
            <option value="KCB">KCB</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Amount Deposited (KES) *</Label>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="banking-amount-input" />
          <label className="mt-1 flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" className="mt-1" checked={part} onChange={(e) => setPart(e.target.checked)} data-testid="banking-part-checkbox" />
            <span>This is only part of their cash. The rest stays on the shifts and is banked in a later deposit (taken oldest shift first, so there is no variance).</span>
          </label>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Paid out of this cash (optional)</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => setPayouts((ps) => [...ps, { category: "", description: "", amount: "" }])} data-testid="banking-add-payout">+ Add payout</Button>
          </div>
          {payouts.map((p, i) => (
            <div key={i} className="grid grid-cols-[1.2fr_1.2fr_0.8fr_auto] gap-2">
              <select className="rounded-md border border-input bg-white px-2 text-sm" value={p.category} onChange={(e) => setPayout(i, { category: e.target.value })} data-testid={`banking-payout-category-${i}`}>
                <option value="">Category…</option>
                {PAYOUT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Input placeholder="Who / what for" value={p.description} onChange={(e) => setPayout(i, { description: e.target.value })} />
              <Input type="number" placeholder="Amount" value={p.amount} onChange={(e) => setPayout(i, { amount: e.target.value })} data-testid={`banking-payout-amount-${i}`} />
              <Button type="button" variant="ghost" size="sm" onClick={() => setPayouts((ps) => ps.filter((_, j) => j !== i))}>✕</Button>
            </div>
          ))}
          {paidOut > 0 && !part && amount && (
            <div className="text-xs text-muted-foreground">Deposited + paid out = KSh {kes((parseFloat(amount) || 0) + paidOut)} against KSh {kes(expected)} expected.</div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Deposit Slip (photo or PDF, optional)</Label>
          <Input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="banking-slip-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Notes (optional)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Bank, teller, reference number…" data-testid="banking-notes-input" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand/90" data-testid="banking-submit-button">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}Record
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
